/**
 * UsersTab — who our users are and what they do.
 *
 * Data: `adminUsersOverview` (functions/analytics) joins Firebase Auth metadata
 * (sign-up, last sign-in, provider), users/{uid} (plan, flags, onboarding),
 * user_activity/{uid} (last active, sessions, active days, searches by day,
 * first-touch source) and this period's usage. Click a row for the timeline
 * (`adminUserDetail`). Admin actions (role, plan, access, reset, welcome,
 * delete, invite) are unchanged, except plan edits no longer wipe Stripe fields.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, TextInput, ScrollView, Platform, useWindowDimensions, ActivityIndicator, Modal } from 'react-native';
import { db, getAdminAuthHeaders } from '../firebaseConfig';
import { doc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import Constants from 'expo-constants';
import { showToast } from '../utils/toast';
import { adminStyles as styles } from './adminStyles';
import { getUsersOverview, getUserDetail } from '../services/launchService';
import Appear from './motion/Appear';
import PressableScale from './motion/PressableScale';
import { A, PLAN_COLOR, Pill, Stat, Sparkline, Panel, Btn, Segments, EmptyState, relTime, shortDate, money } from './admin/adminUi';
import { useUiStyle, setSiteUiStyle, setUserUiStyle } from '../context/UiStyleContext';
import { useAuth } from '../context/AuthContext';
import { resolveUiStyle, uiStyleLabel } from '../utils/uiStyle';
import { uiVar } from './floodlight/uiStyleCss';
import { AV } from './floodlight/vars/admin';

const DAY = 86400000;
const PROTECTED_EMAILS = ['trevorhawkins@dsignable.com'];
// Floodlight vs Classic badge colours (the look itself is utils/uiStyle.js).
export const LOOK_COLOR = { floodlight: '#00D26A', classic: '#a78bfa' };

/** The look a person sees: their own setting, else the site default. */
export const effectiveLook = (u, siteLook) => resolveUiStyle(siteLook, u?.uiStyle);

export function filterAndSortUsers(rows, { q = '', plan = 'all', activity = 'all', role = 'all', look = 'all', sort = 'lastActive' } = {}, now = Date.now(), siteLook = 'floodlight') {
  const needle = q.trim().toLowerCase();
  const ago = (iso) => (iso ? now - Date.parse(iso) : Infinity);
  const out = rows.filter((u) => {
    if (needle && ![u.email, u.name, u.uid, u.source].some((v) => String(v || '').toLowerCase().includes(needle))) return false;
    if (plan === 'paid' && !(u.plan !== 'FREE')) return false;
    if (plan !== 'all' && plan !== 'paid' && u.plan !== plan) return false;
    if (activity === 'active7' && !(ago(u.lastActiveAt) <= 7 * DAY)) return false;
    if (activity === 'dormant' && !(ago(u.lastActiveAt) > 30 * DAY && Number.isFinite(ago(u.lastActiveAt)))) return false;
    if (activity === 'never' && u.totalSearches > 0) return false;
    if (activity === 'new7' && !(ago(u.createdAt) <= 7 * DAY)) return false;
    if (role === 'admin' && u.userType !== 'admin') return false;
    if (role === 'alpha' && !u.alphaAccess) return false;
    if (role === 'beta' && !u.betaAccess) return false;
    if (role === 'suspended' && !u.suspended) return false;
    if (look === 'own' && !u.uiStyle) return false;
    if ((look === 'floodlight' || look === 'classic') && effectiveLook(u, siteLook) !== look) return false;
    return true;
  });
  const key = {
    lastActive: (u) => -(Date.parse(u.lastActiveAt || 0) || 0),
    newest: (u) => -(Date.parse(u.createdAt || 0) || 0),
    searches: (u) => -(u.searches30 || 0),
    sessions: (u) => -(u.sessions || 0),
    email: (u) => String(u.email || u.uid).toLowerCase(),
  }[sort] || ((u) => 0);
  return out.sort((a, b) => { const x = key(a), y = key(b); return x < y ? -1 : x > y ? 1 : 0; });
}

export default function UsersTab() {
  const { width } = useWindowDimensions();
  const wide = width >= 1100;
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({ q: '', plan: 'all', activity: 'all', role: 'all', look: 'all', sort: 'lastActive' });
  const { siteDefault: siteLook } = useUiStyle();
  const { user: me } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPlan, setNewUserPlan] = useState('FREE');
  const [newUserRole, setNewUserRole] = useState('user');
  const [newUserSuspended, setNewUserSuspended] = useState(false);
  const [newUserAlpha, setNewUserAlpha] = useState(false);
  const [newUserBeta, setNewUserBeta] = useState(false);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(null);
  const [resetLinkModal, setResetLinkModal] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [welcomeModal, setWelcomeModal] = useState(null);
  const [welcomeSending, setWelcomeSending] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const out = await getUsersOverview();
      setRows(out.rows || []);
      setMeta({ generatedAt: out.generatedAt, authOnly: out.authOnly });
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let live = true;
    setDetail({ loading: true });
    getUserDetail(selected).then((d) => live && setDetail(d)).catch((e) => live && setDetail({ error: e.message }));
    return () => { live = false; };
  }, [selected]);

  const patchRow = (uid, patch) => setRows((rs) => rs.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));
  const write = async (uid, update, patch, what) => {
    try {
      await updateDoc(doc(db, 'users', uid), { ...update, updatedAt: serverTimestamp() });
      patchRow(uid, patch);
      showToast(`${what} saved`, 'success');
    } catch (e) {
      showToast(`${what} failed: ${e.message}`, 'error');
    }
  };
  const setRole = (uid, role) => write(uid, { userType: role }, { userType: role }, 'Role');
  // Comps: never on top of a paying Stripe subscription (manage that in Stripe);
  // a comp over an ENDED Stripe sub clears its ids so the record is a clean
  // admin grant (Plans then offers a fresh checkout, not a broken "change").
  const setPlan = (uid, planType) => {
    const row = rows.find((r) => r.uid === uid) || {};
    const paying = ['active', 'trialing', 'cancel_pending', 'past_due'].includes(String(row.subscriptionStatus || ''));
    if (row.billingSource === 'stripe' && paying) {
      showToast('This user pays through Stripe. Change or cancel their plan in the Stripe dashboard.', 'error');
      return Promise.resolve();
    }
    const ended = row.billingSource === 'stripe';
    return write(uid, {
      'subscription.planType': planType,
      'subscription.subscriptionStatus': planType === 'FREE' ? 'none' : 'active',
      'subscription.subscriptionUpdatedAt': new Date().toISOString(),
      'subscription.grantedBy': 'admin',
      ...(ended ? { 'subscription.subscriptionId': deleteField(), 'subscription.source': 'admin', 'subscription.livemode': deleteField(), 'subscription.priceId': deleteField() } : {}),
    }, { plan: planType, planStored: planType, ...(ended ? { billingSource: 'admin', livemode: null } : {}) }, 'Plan');
  };
  const setSuspended = (uid, v) => write(uid, { suspended: !!v }, { suspended: !!v }, v ? 'Suspension' : 'Unsuspension');
  const setAlphaAccess = (uid, v) => write(uid, { alphaAccess: !!v }, { alphaAccess: !!v }, 'Alpha access');
  const setBetaAccessUser = (uid, v) => write(uid, { betaAccess: !!v }, { betaAccess: !!v }, 'Beta access');
  const setBillingTester = (uid, v) => write(uid, { billingTester: v ? true : deleteField() }, { billingTester: !!v }, 'Billing tester');
  // look: 'floodlight' | 'classic' | null (follow the site default). Applies live on their next snapshot.
  const setLook = async (uid, look) => {
    try {
      await setUserUiStyle(uid, look);
      patchRow(uid, { uiStyle: look || null });
      showToast(look ? `Look set to ${uiStyleLabel(look)}` : 'Look now follows the site default', 'success');
    } catch (e) {
      showToast(`Look failed: ${e.message}`, 'error');
    }
  };
  const setSiteLook = async (look) => {
    if (look === siteLook) return;
    try {
      await setSiteUiStyle(look, me?.uid || null);
      showToast(`Everyone without their own setting now sees ${uiStyleLabel(look)}`, 'success');
    } catch (e) {
      showToast(`Site look failed: ${e.message}`, 'error');
    }
  };

  const getFunctionsBase = () => (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
  );

  const deleteUser = async (userId, userEmail) => {
    setConfirmDeleteId(null);
    try {
      const base = getFunctionsBase();
      if (!base) throw new Error('Functions base URL not configured');
      const res = await fetch(`${base}/adminDeleteUser`, { method: 'POST', headers: await getAdminAuthHeaders(), body: JSON.stringify({ uid: userId, email: userEmail }) });
      const text = await res.text();
      if (!res.ok) throw new Error(text);
      showToast(`Deleted ${userEmail || userId}`, 'success');
      setSelected(null);
      setRows((rs) => rs.filter((r) => r.uid !== userId));
    } catch (e) {
      showToast(`Error deleting user: ${e.message || 'Unknown error'}`, 'error');
    }
  };

  const sendPasswordResetAdmin = async (email) => {
    const base = getFunctionsBase();
    if (!base) { showToast('Functions base URL not configured.', 'error'); return; }
    try {
      const res = await fetch(`${base}/adminSendPasswordReset`, { method: 'POST', headers: await getAdminAuthHeaders(), body: JSON.stringify({ email }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setResetLinkModal({ email, link: data.resetLink, emailSent: data.emailSent, emailNote: data.emailNote });
      showToast(data.emailSent ? `Reset email sent to ${email}` : `Reset link generated for ${email}`, 'success');
    } catch (e) {
      showToast(`Password reset failed: ${e.message}`, 'error');
    }
  };

  const sendWelcomeEmail = async () => {
    if (!welcomeModal) return;
    const base = getFunctionsBase();
    if (!base) { showToast('Functions base URL not configured.', 'error'); return; }
    setWelcomeSending(true);
    try {
      const res = await fetch(`${base}/adminSendWelcomeEmail`, {
        method: 'POST', headers: await getAdminAuthHeaders(),
        body: JSON.stringify({ uid: welcomeModal.uid, email: welcomeModal.email, tier: welcomeModal.tier, planType: welcomeModal.planType, adminNote: welcomeModal.adminNote || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setWelcomeModal(null);
      showToast(data.emailSent ? `Welcome email sent to ${welcomeModal.email}` : `Tier/plan updated for ${welcomeModal.email} (email not sent — SMTP not configured)`, data.emailSent ? 'success' : 'info');
      load();
    } catch (e) {
      showToast(`Failed to send welcome: ${e.message}`, 'error');
    } finally {
      setWelcomeSending(false);
    }
  };

  const createUserDoc = async () => {
    setCreateError('');
    setCreateSuccess(null);
    if (!newUserEmail.trim()) { setCreateError('Email is required.'); return; }
    setCreateLoading(true);
    try {
      const base = getFunctionsBase();
      if (!base) throw new Error('Functions base URL not configured');
      const payload = { email: newUserEmail.trim(), role: newUserRole, planType: newUserPlan, suspended: !!newUserSuspended, alphaAccess: !!newUserAlpha, betaAccess: !!newUserBeta, password: newUserPassword.trim() || undefined, sendReset: true };
      const res = await fetch(`${base}/adminCreateUserAndInvite`, { method: 'POST', headers: await getAdminAuthHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setCreateSuccess({ email: newUserEmail.trim(), uid: data.uid, resetLink: data.resetLink, emailSent: data.emailSent, emailNote: data.emailNote });
      setNewUserEmail(''); setNewUserPassword(''); setNewUserPlan('FREE'); setNewUserRole('user'); setNewUserSuspended(false); setNewUserAlpha(false); setNewUserBeta(false);
      load();
    } catch (e) {
      setCreateError(e.message || 'Failed to create user.');
    } finally {
      setCreateLoading(false);
    }
  };

  const view = useMemo(() => filterAndSortUsers(rows, filters, Date.now(), siteLook), [rows, filters, siteLook]);
  const lookCounts = useMemo(() => rows.reduce((c, u) => { c[effectiveLook(u, siteLook)] += 1; if (u.uiStyle) c.own += 1; return c; }, { floodlight: 0, classic: 0, own: 0 }), [rows, siteLook]);
  const now = Date.now();
  const kpi = useMemo(() => {
    const within = (iso, d) => iso && now - Date.parse(iso) <= d * DAY;
    return {
      total: rows.length,
      new7: rows.filter((u) => within(u.createdAt, 7)).length,
      active7: rows.filter((u) => within(u.lastActiveAt, 7)).length,
      active30: rows.filter((u) => within(u.lastActiveAt, 30)).length,
      paying: rows.filter((u) => u.billingSource === 'stripe' && u.livemode !== false && u.plan !== 'FREE').length,
      comped: rows.filter((u) => u.billingSource === 'admin' && u.plan !== 'FREE').length,
      searches30: rows.reduce((s, u) => s + (u.searches30 || 0), 0),
      neverSearched: rows.filter((u) => !u.totalSearches && !u.historyCount).length,
    };
  }, [rows, now]);
  const planColors = PLAN_COLOR;
  const planBg = { FREE: 'rgba(148,163,184,0.15)', BASIC: 'rgba(96,165,250,0.15)', PRO: 'rgba(167,139,250,0.15)', ULTIMATE: 'rgba(251,191,36,0.15)' };
  const sel = rows.find((r) => r.uid === selected) || null;
  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <View style={styles.tabWrap}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <View>
          <Text style={styles.sectionTitle}>Users</Text>
          <Text style={{ color: AV.faint, fontSize: 12, marginTop: 2 }}>
            {loading ? 'Loading…' : `${rows.length} accounts · updated ${relTime(meta?.generatedAt)}`}{meta?.authOnly ? ` · ${meta.authOnly} sign-ins without a profile` : ''}
          </Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Btn label="Refresh" onPress={() => { setLoading(true); load(); }} small />
          <Btn label="+ Add user" tone="primary" onPress={() => { setCreateError(''); setModalOpen(true); }} small />
        </View>
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <Stat index={0} label="Accounts" value={kpi.total} hint={`${kpi.new7} new this week`} />
        <Stat index={1} label="Active 7d" value={kpi.active7} hint={`${kpi.active30} in 30 days`} tone={kpi.active7 ? 'good' : undefined} />
        <Stat index={2} label="Paying" value={kpi.paying} hint={`${kpi.comped} on comped plans`} />
        <Stat index={3} label="Searches 30d" value={kpi.searches30} hint={`${kpi.neverSearched} never searched`} />
      </View>

      <View testID="users-site-look" style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: AV.line, backgroundColor: AV.panel }}>
        <View style={{ flexGrow: 1, flexBasis: 220 }}>
          <Text style={{ color: AV.ink, fontSize: 13, fontWeight: '700' }}>Look for everyone</Text>
          <Text style={{ color: AV.faint, fontSize: 11, marginTop: 2 }}>
            Applies to signed-out visitors and everyone without their own setting. Right now: {lookCounts.floodlight} on Floodlight · {lookCounts.classic} on Classic · {lookCounts.own} with their own setting.
          </Text>
        </View>
        <Segments value={siteLook} onChange={setSiteLook} options={[{ value: 'floodlight', label: 'Floodlight' }, { value: 'classic', label: 'Classic' }]} />
      </View>

      <View style={{ gap: 10, marginBottom: 12 }}>
        <TextInput
          value={filters.q} onChangeText={setF('q')} placeholder="Search email, name, uid or source" placeholderTextColor={A.faint}
          style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: AV.line, borderRadius: 10, color: AV.ink, paddingHorizontal: 12, paddingVertical: 9, fontSize: 13 }}
          accessibilityLabel="Search users"
        />
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          <Segments value={filters.activity} onChange={setF('activity')} options={[{ value: 'all', label: 'All' }, { value: 'active7', label: 'Active 7d' }, { value: 'new7', label: 'New 7d' }, { value: 'dormant', label: 'Dormant 30d+' }, { value: 'never', label: 'Never searched' }]} />
          <Segments value={filters.plan} onChange={setF('plan')} options={[{ value: 'all', label: 'Any plan' }, { value: 'paid', label: 'Paid/comped' }, { value: 'FREE', label: 'Free' }, { value: 'BASIC', label: 'Basic' }, { value: 'PRO', label: 'Pro' }, { value: 'ULTIMATE', label: 'Ultimate' }]} />
          <Segments value={filters.role} onChange={setF('role')} options={[{ value: 'all', label: 'Everyone' }, { value: 'admin', label: 'Admins' }, { value: 'alpha', label: 'Alpha' }, { value: 'beta', label: 'Beta' }, { value: 'suspended', label: 'Suspended' }]} />
          <Segments value={filters.look} onChange={setF('look')} options={[{ value: 'all', label: 'Any look' }, { value: 'floodlight', label: 'Floodlight' }, { value: 'classic', label: 'Classic' }, { value: 'own', label: 'Own setting' }]} />
          <Segments value={filters.sort} onChange={setF('sort')} options={[{ value: 'lastActive', label: 'Last active' }, { value: 'newest', label: 'Newest' }, { value: 'searches', label: 'Most searches' }, { value: 'sessions', label: 'Most sessions' }, { value: 'email', label: 'A–Z' }]} />
        </View>
      </View>

      {loading ? (
        <View style={{ padding: 40, alignItems: 'center' }}><ActivityIndicator color={A.accent} /></View>
      ) : loadError ? (
        <EmptyState icon="!" title="Couldn't load users" body={`${loadError}. If the analytics functions aren't deployed yet, deploy them first.`} />
      ) : !view.length ? (
        <EmptyState title="No users match" body="Clear a filter or search for something else." />
      ) : (
        <View style={{ borderRadius: 14, borderWidth: 1, borderColor: AV.line, overflow: 'hidden' }}>
          {wide && (
            <View style={[uStyles.row, uStyles.headRow]}>
              {['User', 'Plan', 'Signed up', 'Last active', 'Activity 30d', 'Searches 14d', 'Onboarding', 'Look'].map((h, i) => (
                <Text key={h} style={[uStyles.th, uStyles[`c${i}`]]}>{h}</Text>
              ))}
            </View>
          )}
          {view.map((u, i) => (
            <Appear key={u.uid} index={Math.min(i, 12)} distance={6}>
              <PressableScale onPress={() => setSelected(u.uid)} scaleTo={0.997} lift={0} accessibilityRole="button" accessibilityLabel={`Open ${u.email || u.uid}`}
                style={[uStyles.row, !wide && uStyles.cardRow, selected === u.uid && { backgroundColor: uiVar('adm-accent-wash-8', 'rgba(167,139,250,0.08)') }]} hoverStyle={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                <View style={[uStyles.c0, { flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
                  <View style={uStyles.avatar}><Text style={uStyles.avatarText}>{(u.name || u.email || '?').slice(0, 1).toUpperCase()}</Text></View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={uStyles.email} numberOfLines={1}>{u.email || u.uid}</Text>
                    <View style={{ flexDirection: 'row', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                      {u.name ? <Text style={uStyles.sub} numberOfLines={1}>{u.name}</Text> : null}
                      {u.userType === 'admin' && <Pill label="ADMIN" color={A.accent} />}
                      {u.alphaAccess && <Pill label="ALPHA" color={A.amber} />}
                      {u.betaAccess && <Pill label="BETA" color={A.blue} />}
                      {u.suspended && <Pill label="SUSPENDED" color={A.red} />}
                    </View>
                  </View>
                </View>
                <View style={uStyles.c1}>
                  <Pill label={u.plan} color={PLAN_COLOR[u.plan] || A.soft} />
                  <Text style={uStyles.sub}>{u.billingSource === 'stripe' ? `Stripe${u.livemode === false ? ' test' : ''} · ${u.subscriptionStatus || ''}` : u.billingSource === 'admin' ? 'Comped' : '—'}</Text>
                </View>
                <View style={uStyles.c2}>
                  <Text style={uStyles.cell}>{shortDate(u.createdAt)}</Text>
                  <Text style={uStyles.sub}>{u.source}{u.providers?.includes('google.com') ? ' · Google' : ''}</Text>
                </View>
                <View style={uStyles.c3}>
                  <Text style={[uStyles.cell, u.lastActiveAt && now - Date.parse(u.lastActiveAt) < 7 * DAY && { color: A.green }]}>{relTime(u.lastActiveAt, now)}</Text>
                  <Text style={uStyles.sub}>{u.sessions ? `${u.sessions} sessions` : 'not tracked yet'}</Text>
                </View>
                <View style={uStyles.c4}>
                  <Text style={uStyles.cell}>{u.activeDays30} days</Text>
                  <Text style={uStyles.sub}>{u.usageThisPeriod} counted this period</Text>
                </View>
                <View style={[uStyles.c5, { flexDirection: 'row', alignItems: 'center', gap: 8 }]}>
                  <Sparkline values={u.searchSpark} />
                  <Text style={uStyles.cell}>{u.searches30}</Text>
                </View>
                <View style={uStyles.c6}>
                  <Text style={[uStyles.cell, { color: u.onboarding === 'done' ? A.green : AV.soft }]}>{u.onboarding}</Text>
                  <Text style={uStyles.sub}>{u.historyCount != null ? `${u.historyCount} in history` : ''}</Text>
                </View>
                <View style={uStyles.c7}>
                  <Pill label={uiStyleLabel(effectiveLook(u, siteLook)).toUpperCase()} color={LOOK_COLOR[effectiveLook(u, siteLook)]} />
                  <Text style={uStyles.sub}>{u.uiStyle ? 'own setting' : 'site default'}</Text>
                </View>
              </PressableScale>
            </Appear>
          ))}
        </View>
      )}

      <UserDrawer
        user={sel} detail={detail} wide={wide} onClose={() => setSelected(null)}
        siteLook={siteLook}
        actions={{ setLook, setRole, setPlan, setSuspended, setAlphaAccess, setBetaAccessUser, setBillingTester, sendPasswordResetAdmin, openWelcome: (u) => setWelcomeModal({ uid: u.uid, email: u.email, displayName: u.name || u.email, tier: u.alphaAccess ? 'alpha' : u.betaAccess ? 'beta' : 'none', planType: u.planStored || 'FREE' }), confirmDeleteId, setConfirmDeleteId, deleteUser }}
      />
      {/* ── Create User Modal ── */}
      {modalOpen && (
        <View style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: 20 }}>
          <View style={{ width: '100%', maxWidth: 500, borderRadius: 16, backgroundColor: uiVar('adm-card', '#16162a'), borderWidth: 1, borderColor: uiVar('adm-accent-wash-30', 'rgba(139,92,246,0.3)'), overflow: 'hidden' }}>
            {/* Modal header */}
            <View style={{ padding: 20, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.08)', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ color: uiVar('adm-ink', '#fff'), fontWeight: '700', fontSize: 17 }}>Create New User</Text>
              <TouchableOpacity onPress={() => setModalOpen(false)} style={{ padding: 4 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={{ maxHeight: 520 }} contentContainerStyle={{ padding: 20, gap: 16 }}>
              {/* Email */}
              <View style={{ gap: 6 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>EMAIL *</Text>
                <TextInput
                  style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 9, color: uiVar('adm-ink', '#fff'), fontSize: 14 }}
                  value={newUserEmail}
                  onChangeText={setNewUserEmail}
                  placeholder="user@example.com"
                  placeholderTextColor="rgba(255,255,255,0.3)"
                  autoCapitalize="none"
                  keyboardType="email-address"
                />
              </View>

              {/* Password */}
              <View style={{ gap: 6 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>TEMP PASSWORD <Text style={{ color: 'rgba(255,255,255,0.3)', fontWeight: '400' }}>(optional — invite email sent if blank)</Text></Text>
                <TextInput
                  style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 9, color: uiVar('adm-ink', '#fff'), fontSize: 14 }}
                  value={newUserPassword}
                  onChangeText={setNewUserPassword}
                  placeholder="Leave blank to send invite email"
                  placeholderTextColor="rgba(255,255,255,0.3)"
                  secureTextEntry
                />
              </View>

              {/* Role + Plan row */}
              <View style={{ flexDirection: 'row', gap: 14 }}>
                <View style={{ flex: 1, gap: 6 }}>
                  <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>ROLE</Text>
                  <View style={{ flexDirection: 'row', gap: 6 }}>
                    {['user', 'admin'].map(r => (
                      <TouchableOpacity key={r} onPress={() => setNewUserRole(r)}
                        style={{ flex: 1, paddingVertical: 8, borderRadius: 8, borderWidth: 1, alignItems: 'center',
                          borderColor: newUserRole === r ? uiVar('adm-accent', '#a78bfa') : 'rgba(255,255,255,0.15)',
                          backgroundColor: newUserRole === r ? uiVar('adm-accent-wash-20', 'rgba(167,139,250,0.2)') : 'transparent' }}>
                        <Text style={{ color: newUserRole === r ? uiVar('adm-accent', '#a78bfa') : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600', textTransform: 'capitalize' }}>{r}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              </View>

              {/* Plan */}
              <View style={{ gap: 6 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>PLAN</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {[['FREE','none'], ['BASIC','active'], ['PRO','active'], ['ULTIMATE','active']].map(([p]) => (
                    <TouchableOpacity key={p} onPress={() => setNewUserPlan(p)}
                      style={{ paddingVertical: 7, paddingHorizontal: 14, borderRadius: 8, borderWidth: 1,
                        borderColor: newUserPlan === p ? (planColors[p] || '#fff') : 'rgba(255,255,255,0.15)',
                        backgroundColor: newUserPlan === p ? (planBg[p] || 'transparent') : 'transparent' }}>
                      <Text style={{ color: newUserPlan === p ? (planColors[p] || uiVar('adm-ink', '#fff')) : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600' }}>{p}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Access tier */}
              <View style={{ gap: 6 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>ACCESS TIER</Text>
                <View style={{ flexDirection: 'row', gap: 6 }}>
                  <TouchableOpacity onPress={() => setNewUserAlpha(v => !v)}
                    style={{ flex: 1, paddingVertical: 8, borderRadius: 8, borderWidth: 1, alignItems: 'center',
                      borderColor: newUserAlpha ? '#4ade80' : 'rgba(255,255,255,0.15)',
                      backgroundColor: newUserAlpha ? 'rgba(74,222,128,0.15)' : 'transparent' }}>
                    <Text style={{ color: newUserAlpha ? '#4ade80' : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600' }}>{newUserAlpha ? 'Alpha ✓' : 'Alpha'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setNewUserBeta(v => !v)}
                    style={{ flex: 1, paddingVertical: 8, borderRadius: 8, borderWidth: 1, alignItems: 'center',
                      borderColor: newUserBeta ? '#fbbf24' : 'rgba(255,255,255,0.15)',
                      backgroundColor: newUserBeta ? 'rgba(251,191,36,0.15)' : 'transparent' }}>
                    <Text style={{ color: newUserBeta ? '#fbbf24' : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600' }}>{newUserBeta ? 'Beta ✓' : 'Beta'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setNewUserSuspended(v => !v)}
                    style={{ flex: 1, paddingVertical: 8, borderRadius: 8, borderWidth: 1, alignItems: 'center',
                      borderColor: newUserSuspended ? '#ef4444' : 'rgba(255,255,255,0.15)',
                      backgroundColor: newUserSuspended ? 'rgba(239,68,68,0.12)' : 'transparent' }}>
                    <Text style={{ color: newUserSuspended ? '#ef4444' : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: '600' }}>{newUserSuspended ? 'Suspended ✓' : 'Suspend'}</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {createError ? <Text style={{ color: '#f87171', fontSize: 13 }}>{createError}</Text> : null}
            </ScrollView>

            {/* Success state — show reset link before closing */}
            {createSuccess && (
              <View style={{ margin: 16, padding: 14, borderRadius: 10, backgroundColor: 'rgba(74,222,128,0.08)', borderWidth: 1, borderColor: 'rgba(74,222,128,0.3)' }}>
                <Text style={{ color: '#4ade80', fontWeight: '700', fontSize: 13, marginBottom: 6 }}>User created successfully</Text>
                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 4 }}>
                  {createSuccess.emailSent ? `Invite email sent to ${createSuccess.email}` : `Email not sent (${createSuccess.emailNote || 'SMTP not configured'}).`}
                </Text>
                {createSuccess.resetLink ? (
                  <View style={{ marginTop: 8 }}>
                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 4 }}>Password reset link (copy and share):</Text>
                    <Text
                      selectable
                      style={{ color: uiVar('adm-accent', '#a78bfa'), fontSize: 11, backgroundColor: 'rgba(255,255,255,0.05)', padding: 8, borderRadius: 6, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}
                    >{createSuccess.resetLink}</Text>
                  </View>
                ) : null}
              </View>
            )}

            {/* Modal footer */}
            <View style={{ padding: 16, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)', flexDirection: 'row', justifyContent: 'flex-end', gap: 10 }}>
              <TouchableOpacity onPress={() => { setModalOpen(false); setCreateSuccess(null); setCreateError(''); }}
                style={{ paddingVertical: 9, paddingHorizontal: 18, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)' }}>
                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: '600' }}>{createSuccess ? 'Done' : 'Cancel'}</Text>
              </TouchableOpacity>
              {!createSuccess && (
                <TouchableOpacity onPress={createUserDoc} disabled={createLoading}
                  style={{ paddingVertical: 9, paddingHorizontal: 22, borderRadius: 8, backgroundColor: createLoading ? uiVar('adm-accent-wash-30', 'rgba(139,92,246,0.3)') : uiVar('adm-accent-wash-60', 'rgba(139,92,246,0.6)'), borderWidth: 1, borderColor: uiVar('adm-accent-wash-80', 'rgba(139,92,246,0.8)') }}>
                  <Text style={{ color: createLoading ? 'rgba(255,255,255,0.4)' : uiVar('adm-ink', '#fff'), fontSize: 13, fontWeight: '700' }}>{createLoading ? 'Creating…' : 'Create + Send Invite'}</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      )}

      {/* ── Welcome Email Confirmation Modal ── */}
      {welcomeModal && (
        <View style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', justifyContent: 'center', alignItems: 'center', zIndex: 1002, padding: 20 }}>
          <View style={{ width: '100%', maxWidth: 480, borderRadius: 16, backgroundColor: uiVar('adm-card', '#16162a'), borderWidth: 1, borderColor: 'rgba(52,211,153,0.35)', padding: 28 }}>
            <Text style={{ color: uiVar('adm-ink', '#fff'), fontWeight: '800', fontSize: 18, marginBottom: 4 }}>Send Welcome Email</Text>
            <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, marginBottom: 24 }}>{welcomeModal.email}</Text>

            {/* Tier selector */}
            <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Access Tier</Text>
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 20 }}>
              {[
                { key: 'alpha', label: 'Alpha', color: '#a78bfa', bg: 'rgba(167,139,250,0.15)', border: 'rgba(167,139,250,0.5)' },
                { key: 'beta', label: 'Beta', color: '#60a5fa', bg: 'rgba(96,165,250,0.15)', border: 'rgba(96,165,250,0.5)' },
                { key: 'user', label: 'User', color: '#34d399', bg: 'rgba(52,211,153,0.15)', border: 'rgba(52,211,153,0.5)' },
              ].map(({ key, label, color, bg, border }) => (
                <TouchableOpacity
                  key={key}
                  onPress={() => setWelcomeModal(m => ({ ...m, tier: key }))}
                  style={{ flex: 1, paddingVertical: 10, borderRadius: 10, borderWidth: 2,
                    borderColor: welcomeModal.tier === key ? border : 'rgba(255,255,255,0.1)',
                    backgroundColor: welcomeModal.tier === key ? bg : 'transparent',
                    alignItems: 'center' }}
                >
                  <Text style={{ color: welcomeModal.tier === key ? color : 'rgba(255,255,255,0.4)', fontWeight: '700', fontSize: 13 }}>{label}</Text>
                  {welcomeModal.tier === key && (
                    <Text style={{ color, fontSize: 10, marginTop: 2, fontWeight: '600' }}>
                      {key === 'alpha' ? 'Early access' : key === 'beta' ? 'Beta access' : 'Full access'}
                    </Text>
                  )}
                </TouchableOpacity>
              ))}
            </View>

            {/* Plan selector */}
            <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Plan</Text>
            <View style={{ flexDirection: 'row', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
              {['FREE', 'BASIC', 'PRO', 'ULTIMATE'].map(p => (
                <TouchableOpacity
                  key={p}
                  onPress={() => setWelcomeModal(m => ({ ...m, planType: p }))}
                  style={{ paddingVertical: 7, paddingHorizontal: 14, borderRadius: 8, borderWidth: 1.5,
                    borderColor: welcomeModal.planType === p ? (planColors[p] || '#fff') : 'rgba(255,255,255,0.1)',
                    backgroundColor: welcomeModal.planType === p ? (planBg[p] || 'rgba(255,255,255,0.08)') : 'transparent' }}
                >
                  <Text style={{ color: welcomeModal.planType === p ? (planColors[p] || uiVar('adm-ink', '#fff')) : 'rgba(255,255,255,0.4)', fontWeight: '700', fontSize: 12 }}>{p}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Optional note */}
            <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Personal Note (optional)</Text>
            <TextInput
              value={welcomeModal.adminNote || ''}
              onChangeText={v => setWelcomeModal(m => ({ ...m, adminNote: v }))}
              placeholder="Add a personal note to the welcome email…"
              placeholderTextColor="rgba(255,255,255,0.25)"
              multiline
              numberOfLines={3}
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 10, color: uiVar('adm-ink', '#fff'), fontSize: 13, minHeight: 70, marginBottom: 24 }}
            />

            {/* Summary */}
            <View style={{ padding: 12, borderRadius: 10, backgroundColor: 'rgba(52,211,153,0.07)', borderWidth: 1, borderColor: 'rgba(52,211,153,0.2)', marginBottom: 20 }}>
              <Text style={{ color: '#34d399', fontSize: 13, fontWeight: '600' }}>
                This will set {welcomeModal.email} as a{welcomeModal.tier === 'alpha' ? 'n' : ''} <Text style={{ fontWeight: '800' }}>{welcomeModal.tier.charAt(0).toUpperCase() + welcomeModal.tier.slice(1)}</Text> tester on the <Text style={{ fontWeight: '800' }}>{welcomeModal.planType}</Text> plan and send them a welcome email.
              </Text>
            </View>

            <View style={{ flexDirection: 'row', gap: 10, justifyContent: 'flex-end' }}>
              <TouchableOpacity
                onPress={() => setWelcomeModal(null)}
                style={{ paddingVertical: 10, paddingHorizontal: 20, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.08)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)' }}
              >
                <Text style={{ color: 'rgba(255,255,255,0.6)', fontWeight: '600', fontSize: 13 }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={sendWelcomeEmail}
                disabled={welcomeSending}
                style={{ paddingVertical: 10, paddingHorizontal: 24, borderRadius: 8, backgroundColor: welcomeSending ? 'rgba(52,211,153,0.3)' : '#34d399' }}
              >
                <Text style={{ color: welcomeSending ? 'rgba(255,255,255,0.5)' : '#0f0f17', fontWeight: '800', fontSize: 13 }}>
                  {welcomeSending ? 'Sending…' : 'Confirm & Send'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* ── Reset Link Modal (shown after Send Password Reset) ── */}
      {resetLinkModal && (
        <View style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', justifyContent: 'center', alignItems: 'center', zIndex: 1001, padding: 20 }}>
          <View style={{ width: '100%', maxWidth: 460, borderRadius: 16, backgroundColor: uiVar('adm-card', '#16162a'), borderWidth: 1, borderColor: 'rgba(251,191,36,0.3)', padding: 24 }}>
            <Text style={{ color: uiVar('adm-ink', '#fff'), fontWeight: '700', fontSize: 16, marginBottom: 4 }}>Password Reset</Text>
            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 16 }}>{resetLinkModal.email}</Text>
            <View style={{ padding: 12, borderRadius: 8, backgroundColor: resetLinkModal.emailSent ? 'rgba(74,222,128,0.08)' : 'rgba(251,191,36,0.08)', borderWidth: 1, borderColor: resetLinkModal.emailSent ? 'rgba(74,222,128,0.3)' : 'rgba(251,191,36,0.3)', marginBottom: 16 }}>
              <Text style={{ color: resetLinkModal.emailSent ? '#4ade80' : '#fbbf24', fontWeight: '600', fontSize: 13 }}>
                {resetLinkModal.emailSent ? 'Reset email sent successfully' : `Email not sent — ${resetLinkModal.emailNote || 'SMTP not configured'}`}
              </Text>
            </View>
            {resetLinkModal.link && (
              <View style={{ marginBottom: 16 }}>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 6 }}>Reset link (expires in 1 hour) — copy and share manually if needed:</Text>
                <Text
                  selectable
                  style={{ color: uiVar('adm-accent', '#a78bfa'), fontSize: 11, backgroundColor: 'rgba(255,255,255,0.05)', padding: 10, borderRadius: 8, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, lineHeight: 18 }}
                >{resetLinkModal.link}</Text>
              </View>
            )}
            <TouchableOpacity
              onPress={() => setResetLinkModal(null)}
              style={{ paddingVertical: 10, paddingHorizontal: 20, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.1)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)', alignSelf: 'flex-end' }}
            >
              <Text style={{ color: uiVar('adm-ink', '#fff'), fontWeight: '600', fontSize: 13 }}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

function Fact({ label, value, tone }) {
  return (
    <View style={{ flexBasis: 150, flexGrow: 1, gap: 2 }}>
      <Text style={{ color: AV.faint, fontSize: 10, fontWeight: '800', letterSpacing: 1, textTransform: 'uppercase' }}>{label}</Text>
      <Text style={{ color: tone || AV.ink, fontSize: 13 }} selectable>{value ?? '—'}</Text>
    </View>
  );
}

function UserDrawer({ user, detail, wide, onClose, actions, siteLook }) {
  if (!user) return null;
  const a = actions;
  const protectedUser = PROTECTED_EMAILS.includes(String(user.email || '').toLowerCase());
  const features = Object.entries(user.featureCounts || {}).sort((x, y) => y[1] - x[1]);
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.55)' }}>
        <TouchableOpacity style={{ flex: 1 }} onPress={onClose} accessibilityLabel="Close user details" />
        <Appear from="right" distance={24} style={{ width: wide ? 560 : '100%', height: '100%', backgroundColor: uiVar('adm-card', '#11111b'), borderLeftWidth: 1, borderLeftColor: AV.line }}>
          <ScrollView contentContainerStyle={{ padding: 20, gap: 14 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <View style={[uStyles.avatar, { width: 44, height: 44, borderRadius: 22 }]}><Text style={[uStyles.avatarText, { fontSize: 18 }]}>{(user.name || user.email || '?').slice(0, 1).toUpperCase()}</Text></View>
              <View style={{ flex: 1 }}>
                <Text style={{ color: AV.ink, fontSize: 17, fontWeight: '800' }} selectable numberOfLines={1}>{user.email || user.uid}</Text>
                <Text style={{ color: AV.faint, fontSize: 12 }} selectable>{user.name ? `${user.name} · ` : ''}{user.uid}</Text>
              </View>
              <Btn label="Close" small onPress={onClose} />
            </View>

            <Panel title="Account">
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
                <Fact label="Signed up" value={`${shortDate(user.createdAt)} (${relTime(user.createdAt)})`} />
                <Fact label="Last active" value={relTime(user.lastActiveAt)} tone={user.lastActiveAt && Date.now() - Date.parse(user.lastActiveAt) < 7 * DAY ? A.green : undefined} />
                <Fact label="Last sign-in" value={relTime(user.lastSignInAt)} />
                <Fact label="Sign-in method" value={(user.providers || []).join(', ') || '—'} />
                <Fact label="Source" value={user.source} />
                <Fact label="Landing page" value={user.attribution?.landing || '—'} />
                <Fact label="Campaign" value={[user.attribution?.utm_campaign, user.attribution?.utm_medium].filter(Boolean).join(' / ') || '—'} />
                <Fact label="Onboarding" value={user.onboarding} />
              </View>
            </Panel>

            <Panel title="Usage">
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
                <Fact label="Sessions" value={user.sessions} />
                <Fact label="Active days (30d)" value={user.activeDays30} />
                <Fact label="Searches (30d)" value={user.searches30} />
                <Fact label="Searches tracked" value={user.totalSearches} />
                <Fact label="Query history" value={user.historyCount} />
                <Fact label="Counted this period" value={user.usageThisPeriod} />
              </View>
              <Sparkline values={user.searchSpark} width={300} height={40} />
              {!!features.length && (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {features.map(([k, v]) => <Pill key={k} label={`${k.replace(/_/g, ' ')} · ${v}`} color={A.soft} />)}
                </View>
              )}
              {!!detail?.usage?.length && <Text style={{ color: AV.faint, fontSize: 12 }}>By period: {detail.usage.slice(0, 6).map((p) => `${p.period} ${p.count}`).join(' · ')}</Text>}
            </Panel>

            <Panel title="Plan & billing">
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
                <Fact label="Effective plan" value={user.plan} tone={PLAN_COLOR[user.plan]} />
                <Fact label="Source" value={user.billingSource === 'stripe' ? `Stripe ${user.livemode === false ? '(test)' : '(live)'}` : user.billingSource === 'admin' ? 'Comped by admin' : 'None'} />
                <Fact label="Status" value={user.subscriptionStatus || '—'} />
                <Fact label="Billing period" value={user.interval ? (user.interval === 'year' ? 'Yearly' : 'Monthly') : '—'} />
                <Fact label="Renews / ends" value={shortDate(user.currentPeriodEnd)} />
              </View>
              {!!detail?.billing?.length && detail.billing.slice(0, 8).map((b) => (
                <Text key={b.id} style={{ color: AV.soft, fontSize: 12, fontFamily: A.mono }}>{shortDate(b.processedAt)} · {b.type}{b.amount ? ` · ${money(b.amount)}` : ''}{b.livemode === false ? ' · test' : ''}</Text>
              ))}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                {['FREE', 'BASIC', 'PRO', 'ULTIMATE'].map((p) => (
                  <Btn key={p} small label={p === user.planStored ? `${p} ✓` : p} tone={p === user.planStored ? 'primary' : 'default'} onPress={() => a.setPlan(user.uid, p)} accessibilityLabel={`Comp ${p}`} />
                ))}
              </View>
              {user.billingSource === 'stripe' && <Text style={{ color: A.amber, fontSize: 11 }}>Stripe-billed: change or cancel a paying subscription in the Stripe dashboard. Comps only apply once their subscription has ended.</Text>}
            </Panel>

            <Panel title="Look" subtitle={`Sees ${uiStyleLabel(effectiveLook(user, siteLook))} ${user.uiStyle ? '(own setting)' : '(site default)'}. Same features either way.`}>
              <View testID="user-look" style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                {[[null, `Site default (${uiStyleLabel(siteLook)})`], ['floodlight', 'Floodlight'], ['classic', 'Classic']].map(([v, l]) => {
                  const on = (user.uiStyle || null) === v;
                  return <Btn key={l} small label={on ? `${l} ✓` : l} tone={on ? 'primary' : 'default'} onPress={() => a.setLook(user.uid, v)} accessibilityLabel={`Look: ${l}`} />;
                })}
              </View>
            </Panel>

            <Panel title="Access">
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                <Btn small label={user.userType === 'admin' ? 'Make user' : 'Make admin'} onPress={() => a.setRole(user.uid, user.userType === 'admin' ? 'user' : 'admin')} />
                <Btn small label={user.alphaAccess ? 'Remove alpha' : 'Grant alpha'} onPress={() => a.setAlphaAccess(user.uid, !user.alphaAccess)} />
                <Btn small label={user.betaAccess ? 'Remove beta' : 'Grant beta'} onPress={() => a.setBetaAccessUser(user.uid, !user.betaAccess)} />
                <Btn small label={user.billingTester ? 'Remove billing tester' : 'Billing tester'} onPress={() => a.setBillingTester(user.uid, !user.billingTester)} />
                <Btn small tone={user.suspended ? 'good' : 'danger'} label={user.suspended ? 'Unsuspend' : 'Suspend'} onPress={() => a.setSuspended(user.uid, !user.suspended)} />
              </View>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                {!!user.email && <Btn small label="Send password reset" onPress={() => a.sendPasswordResetAdmin(user.email)} />}
                {!!user.email && <Btn small label="Welcome email…" onPress={() => a.openWelcome(user)} />}
                {!protectedUser && (a.confirmDeleteId === user.uid
                  ? <><Btn small tone="danger" label="Confirm delete" onPress={() => a.deleteUser(user.uid, user.email)} /><Btn small label="Cancel" onPress={() => a.setConfirmDeleteId(null)} /></>
                  : <Btn small tone="danger" label="Delete account" onPress={() => a.setConfirmDeleteId(user.uid)} />)}
              </View>
            </Panel>

            <Panel title="Timeline" subtitle="Latest tracked events and saved searches">
              {detail?.loading && <ActivityIndicator color={A.accent} />}
              {detail?.error && <Text style={{ color: A.red, fontSize: 12 }}>{detail.error}</Text>}
              {!!detail?.history?.length && detail.history.slice(0, 12).map((h, i) => (
                <View key={`h${i}`} style={{ flexDirection: 'row', gap: 8 }}>
                  <Text style={{ color: AV.faint, fontSize: 11, width: 74 }}>{relTime(h.createdAt)}</Text>
                  <Text style={{ color: AV.ink, fontSize: 12, flex: 1 }} numberOfLines={1}>“{h.prompt || '—'}”</Text>
                  <Text style={{ color: h.resultsCount ? A.green : A.amber, fontSize: 11 }}>{h.resultsCount ?? '?'} results</Text>
                </View>
              ))}
              {!!detail?.events?.length && detail.events.slice(0, 20).map((e, i) => (
                <View key={`e${i}`} style={{ flexDirection: 'row', gap: 8 }}>
                  <Text style={{ color: AV.faint, fontSize: 11, width: 74 }}>{relTime(e.ts)}</Text>
                  <Text style={{ color: AV.soft, fontSize: 12, flex: 1 }} numberOfLines={1}>{e.name.replace(/_/g, ' ')}{e.path ? ` · ${e.path}` : ''}{e.props?.screen ? ` · ${e.props.screen}` : ''}</Text>
                </View>
              ))}
              {detail && !detail.loading && !detail.error && !detail.history?.length && !detail.events?.length && (
                <Text style={{ color: AV.faint, fontSize: 12 }}>Nothing tracked yet. Activity tracking started with this release; saved search history appears once they search.</Text>
              )}
            </Panel>
          </ScrollView>
        </Appear>
      </View>
    </Modal>
  );
}

const uStyles = {
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: AV.line, flexWrap: 'wrap' },
  cardRow: { alignItems: 'flex-start' },
  headRow: { backgroundColor: 'rgba(255,255,255,0.03)', paddingVertical: 9 },
  th: { color: AV.faint, fontSize: 10, fontWeight: '800', letterSpacing: 1, textTransform: 'uppercase' },
  c0: { flex: 2.4, minWidth: 220 },
  c1: { flex: 1, minWidth: 110, gap: 3 },
  c2: { flex: 1, minWidth: 110 },
  c3: { flex: 1, minWidth: 100 },
  c4: { flex: 1, minWidth: 110 },
  c5: { flex: 1.3, minWidth: 130 },
  c6: { flex: 1, minWidth: 100 },
  c7: { flex: 0.9, minWidth: 96, gap: 3 },
  avatar: { width: 32, height: 32, borderRadius: 16, backgroundColor: uiVar('adm-accent-wash-18', 'rgba(167,139,250,0.18)'), alignItems: 'center', justifyContent: 'center' },
  avatarText: { color: uiVar('adm-accent-ink', '#c4b5fd'), fontWeight: '800', fontSize: 13 },
  email: { color: AV.ink, fontSize: 13, fontWeight: '600' },
  sub: { color: AV.faint, fontSize: 11, marginTop: 2 },
  cell: { color: AV.ink, fontSize: 13 },
};
